import React from 'react';
import { getAlert, listAlertEvents, updateAlert } from '../../api/alertsApi';
import AlertsWidget from '../../components/alerts/AlertsWidget';
import AccountSelection from '../../components/AccountSelection';
import PageBackButton from '../../components/PageBackButton';
import { listWatchlistItems } from '../../api/api';
import { toast } from 'sonner';

export default function AlertDetailPage() {
  // Prefer React Router params if available; fallback to parsing URL
  const idFromPath = (() => {
    try {
      const m = window.location.pathname.match(/\/alerts\/(.+)$/);
      return m ? m[1] : undefined;
    } catch { return undefined; }
  })();
  const [id] = React.useState(idFromPath);
  const [alert, setAlert] = React.useState();
  const [events, setEvents] = React.useState([]);
  const [watchlist, setWatchlist] = React.useState([]);
  const [accountId, setAccountId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState();

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true); setError(undefined);
      try {
        const a = await getAlert(id);
        const ev = await listAlertEvents(id);
        if (!mounted) return;
        setAlert(a);
        setEvents(Array.isArray(ev) ? ev : (ev?.results || []));
      } catch (e) { if (mounted) setError(e?.message || 'Failed to load'); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Fetch user's watchlist for symbol selection
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await listWatchlistItems();
        const items = Array.isArray(resp) ? resp : resp?.data;
        if (mounted) setWatchlist(Array.isArray(items) ? items : []);
      } catch {
        if (mounted) setWatchlist([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="p-4 space-y-3">
      <PageBackButton label="Back to Market Alerts" to="/alerts" preferHistory={false} hideLabelOnMobile />
      {loading && <div className="text-sm text-textSecondary">Loading…</div>}
      {error && <div className="text-sm text-red-500">{error}</div>}
      {alert && (
        <div className="rounded border border-borderDefault p-3 bg-card space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-semibold">Edit alert</div>
              <div className="text-sm text-textSecondary">ID #{alert.id}</div>
            </div>
            <div className="min-w-[200px]">
              <AccountSelection
                selectedAccount={accountId ?? alert.account_id}
                setSelectedAccount={setAccountId}
                displayMode="compact"
              />
            </div>
          </div>
          {(() => {
            const spec = alert?.sectioned_spec || {};
            const hasAdvanced = Boolean(spec?.entry_long || spec?.entry_short || spec?.exit_long || spec?.exit_short);
            const hasSimple = Boolean(spec?.entry && Array.isArray(spec.entry.clauses));

            const toUiOperand = (op) => {
              if (op == null) return 0;
              if (typeof op === 'number') return op;
              if (typeof op !== 'object') return op;
              if (op.type === 'price') {
                return { type: 'price', source: op.source || 'close', timeframe: op.timeframe };
              }
              if (op.type === 'indicator' || op.name) {
                let input;
                const inputsArr = Array.isArray(op.inputs) ? op.inputs : undefined;
                if (inputsArr && inputsArr.length > 0) {
                  const first = inputsArr[0];
                  if (first?.ref === 'PRICE') input = { type: 'price', source: first?.params?.field || 'close' };
                }
                return {
                  type: 'indicator',
                  name: String(op.name || '').toLowerCase(),
                  params: op.params || {},
                  input,
                  timeframe: op.timeframe,
                  output: op.output,
                };
              }
              return op;
            };

            const commonProps = {
              initialName: alert.name,
              initialSymbol: alert.symbol,
              initialTimeframe: alert.timeframe,
              initialIsActive: alert.is_active,
              initialPriceSource: alert.price_source || 'AUTO',
              initialTriggerMode: alert.trigger_mode || 'RECURRING',
              initialCooldownSeconds: alert.cooldown_seconds || 0,
              initialExpiresAt: alert.expires_at,
              submitLabel: 'Save changes',
              watchlist,
              accountId: accountId ?? alert.account_id,
              onBuild: async (payload) => {
                try {
                  const patched = await updateAlert(alert.id, payload);
                  setAlert(patched);
                  toast.success('Alert updated');
                } catch (err) {
                  toast.error(err?.message || 'Failed to update alert');
                }
              },
            };

            if (hasSimple) {
              const initialSimpleRules = (spec.entry.clauses || []).map((c) => ({
                lhs: toUiOperand(c.lhs),
                op: c.op,
                rhs: toUiOperand(c.rhs),
              }));
              return (
                <AlertsWidget
                  {...commonProps}
                  initialTab="simple"
                  initialSimpleRules={initialSimpleRules}
                />
              );
            }

            return (
              <AlertsWidget
                {...commonProps}
                initialTab={hasAdvanced ? 'advanced' : 'advanced'}
                initialAdvSpec={spec}
              />
            );
          })()}
        </div>
      )}
  <div className="rounded border border-borderDefault">
        <div className="px-3 py-2 border-b border-borderDefault bg-muted text-sm font-medium">Recent events</div>
        <div className="divide-y divide-borderDefault">
          {events.map((ev) => (
            <div key={ev.id} className="px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="font-medium">{new Date(ev.ts || ev.created_at).toLocaleString()}</div>
                <div className="text-xs text-textSecondary">#{ev.id}</div>
              </div>
              <pre className="mt-1 text-xs text-textSecondary bg-background p-2 rounded overflow-auto max-h-48">{JSON.stringify(ev.payload || ev, null, 2)}</pre>
            </div>
          ))}
          {events.length === 0 && <div className="px-3 py-6 text-sm text-textSecondary">No events yet.</div>}
        </div>
      </div>
    </div>
  );
}
